<template>
    <div :class="marketingDomainItemClasses">
        <template v-if="!domain.isValidated">
            <div class="marketing-domain-item__input">
                <CompassFormItem
                    :label="
                        $t.__(
                            'pages.account.upsell.marketing.ChargebeeUpsellMarketingDomainsItem.domain_name',
                        )
                    "
                    class="inputDomainName"
                >
                    <CompassInput
                        :modelValue="domain.name"
                        placeholder=""
                        @update:modelValue="onUpdateNameDomain"
                        @keypress.space.prevent
                    />
                </CompassFormItem>
                <CompassSelect
                    :modelValue="domain.extension"
                    @update:modelValue="onUpdateExtensionDomain"
                >
                    <CompassDropdownOption
                        v-for="extension in listDomainExtensions"
                        :key="extension.value"
                        :label="extension.label"
                        :value="extension.value"
                    />
                </CompassSelect>
                <CompassPlainButton
                    :disabled="!domain.sender"
                    :label="
                        $t.__(
                            'pages.account.upsell.marketing.ChargebeeUpsellMarketingDomainsItem.check_availability',
                        )
                    "
                    :loading="domainCheckLoading"
                    @click="checkDomain"
                />
            </div>
            <div
                v-if="domainAvailabilityStatus === EnumDomainAvailabilityStatus.AVAILABLE"
                class="marketing-domain-item__availability"
            >
                <CompassIcon
                    class="marketing-domain-item__availability-icon marketing-domain-item__availability-icon--success"
                    icon="success-circle-fill"
                    type="success"
                />
                <span
                    class="typographies-paragraph-medium-semibold marketing-domain-item__availability-label"
                >
                    {{ $l10n.__('Disponible') }}
                </span>
                <CompassButton
                    :label="
                        $t.__(
                            'pages.account.upsell.marketing.ChargebeeUpsellMarketingDomainsItem.select_domain_name',
                        )
                    "
                    @click="onUpdateIsValidated"
                />
            </div>
            <div
                v-else-if="domainAvailabilityStatus === EnumDomainAvailabilityStatus.UNAVAILABLE"
                class="marketing-domain-item__availability"
            >
                <CompassIcon
                    class="marketing-domain-item__availability-icon"
                    icon="warning-fill"
                    type="warning"
                />
                <span
                    class="typographies-paragraph-medium-semibold marketing-domain-item__availability-label"
                >
                    {{ $l10n.__('Indisponible') }}
                </span>
            </div>
        </template>
        <div v-else>
            <div class="validated-title">
                <span class="typographies-paragraph-medium-semibold">
                    {{ domain.sender }}
                </span>
                <CompassButton
                    :label="$t.__('common.actions.delete')"
                    leftIcon="bin"
                    type="tertiary-destructive"
                    @click="onRemoveDomain"
                />
            </div>
            <div class="email-redirect">
                <span class="typographies-paragraph-medium-semibold">
                    {{ $l10n.__('Sous domaine') }}
                </span>
                <div class="email-redirect-input">
                    <CompassInput
                        :modelValue="domain.subDomain"
                        placeholder=""
                        @update:modelValue="onUpdateSubDomain"
                        @keypress.space.prevent
                    />
                    <span class="typographies-paragraph-medium-semibold">
                        {{ `.${domain.sender}` }}
                    </span>
                </div>
                <div class="typographies-paragraph-small-regular email-redirect-description">
                    <p>
                        {{ $l10n.__('Une fois votre achat validé :') }}
                    </p>
                    <ul>
                        <li>
                            {{
                                $l10n.__(
                                    'le sous-domaine sera exclusivement réservé au suivi des ouvertures et clics',
                                )
                            }}
                        </li>
                        <li>
                            {{
                                $l10n.__(
                                    'vous pourrez ajouter vos adresses d’envoi (utilisant votre domaine) dans Sellsy Marketing à la rubrique Plus > Paramètres > Emailing > Adresse(s) email(s) d’envoi.',
                                )
                            }}
                        </li>
                        <li>
                            {{
                                $l10n.__(
                                    'il vous faudra attendre 48h avant pouvoir profiter de votre domaine et débuter les envois d’emails.',
                                )
                            }}
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { createNamespacedHelpers } from 'vuex';
import { debounce } from 'lodash';
const chargebeeUpsellStore = createNamespacedHelpers('chargebeeUpsellStore');
import { checkDomainAvailability } from 'api/chargebee/chargebeeService';

const EnumDomainAvailabilityStatus = Object.freeze({
    UNCHECKED: 'UNCHECKED',
    AVAILABLE: 'AVAILABLE',
    UNAVAILABLE: 'UNAVAILABLE',
});
export default {
    props: {
        domain: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            domainCheckLoading: false,
            domainAvailabilityStatus: EnumDomainAvailabilityStatus.UNCHECKED,
            EnumDomainAvailabilityStatus,
            listDomainExtensions: [
                {
                    label: '.fr',
                    value: 'FR',
                },
                {
                    label: '.com',
                    value: 'COM',
                },
                {
                    label: '.be',
                    value: 'BE',
                },
                {
                    label: '.net',
                    value: 'NET',
                },
                {
                    label: '.org',
                    value: 'ORG',
                },
            ],
        };
    },
    computed: {
        marketingDomainItemClasses() {
            return ['marketing-domain-item', this.domain.isValidated ? 'validated' : ''];
        },
    },
    methods: {
        ...chargebeeUpsellStore.mapActions(['updateDomain']),
        getSenderDomain(extension) {
            const extensionLabel =
                this.listDomainExtensions.find((domain) => domain.value === extension)?.label ??
                '.fr';
            return `${this.domain.name}${extensionLabel}`;
        },
        onUpdateExtensionDomain(value) {
            this.domainAvailabilityStatus = EnumDomainAvailabilityStatus.UNCHECKED;
            this.updateDomain({
                ...this.domain,
                extension: value,
                sender: this.getSenderDomain(value),
            });
        },
        onUpdateNameDomain: debounce(function (value) {
            this.domainAvailabilityStatus = EnumDomainAvailabilityStatus.UNCHECKED;
            this.updateDomain({
                ...this.domain,
                name: value,
                sender: this.getSenderDomain(this.domain.extension),
            });
        }, 200),
        onUpdateIsValidated() {
            this.updateDomain({ ...this.domain, isValidated: true, subDomain: 'info' });
            this.$emit('domainValidated');
        },
        onUpdateSubDomain(value) {
            this.updateDomain({ ...this.domain, subDomain: value });
        },
        async checkDomain() {
            try {
                this.domainCheckLoading = true;
                const status = await checkDomainAvailability(this.domain.sender);
                if (status === 202) {
                    this.domainAvailabilityStatus = EnumDomainAvailabilityStatus.AVAILABLE;
                } else if (status === 423) {
                    this.domainAvailabilityStatus = EnumDomainAvailabilityStatus.UNAVAILABLE;
                }
            } catch (e) {
                this.$pushToast({
                    type: 'error',
                    text: this.$l10n.__(
                        'Une erreur est survenue lors de la vérification du nom de domaine',
                    ),
                });
                this.domainAvailabilityStatus = EnumDomainAvailabilityStatus.UNCHECKED;
            } finally {
                this.domainCheckLoading = false;
            }
        },
        onRemoveDomain() {
            this.$emit('onRemoveDomain');
        },
    },
};
</script>

<style lang="scss" scoped>
.marketing-domain-item {
    background: var(--colors-common-background-primary-subtle);
    border: 1px solid var(--colors-common-border-primary-disabled);
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-1);
    padding: var(--spacing-3);

    p {
        margin: 0;
    }

    &__input {
        align-items: flex-end;
        display: flex;
        gap: var(--spacing-1);

        .inputDomainName {
            flex-grow: 1;
        }

        .compass-select {
            width: 120px;
        }
    }

    &__availability {
        align-items: center;
        display: flex;

        &-icon {
            margin-right: var(--spacing-0);

            &--success {
                color: var(--colors-common-background-success-default);
            }
        }

        &-label {
            margin-right: var(--spacing-3);
        }
    }

    .validated-title {
        align-items: center;
        border-bottom: 1px solid var(--colors-common-border-lighter);
        display: flex;
        gap: var(--spacing-1);
        justify-content: space-between;
        margin-bottom: var(--spacing-1);
        padding-bottom: var(--spacing-1);

        .compass-button {
            flex-shrink: 0;
        }
    }

    .email-redirect {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-0);

        &-input > .compass-input {
            width: 240px;
        }

        &-description {
            color: var(--colors-common-text-subtle-default);
            p {
                margin-bottom: var(--spacing-0);
            }
            ul {
                margin: 0;
                padding: revert;
                list-style-type: initial;
            }
        }
    }

    &.validated {
        background: var(--colors-common-background-subtle);
        border: 1px solid var(--colors-common-border-default);
        padding: var(--spacing-1) var(--spacing-3) var(--spacing-3);
    }
}
</style>
